import React, { useState } from 'react';
import { ChevronDown, Menu, X, MessageSquare, Instagram, Bot, Users, Zap, Calendar, CreditCard, Building } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { user, login, logout } = useAuth();
  const { currentLanguage, languages, setLanguage, t } = useLanguage();
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center relative h-20 w-48">
            <Link to="/" className="flex items-center absolute left-0 top-1/2 -translate-y-1/2 z-10 py-4">
              <img
                src="/logo-azul-transparente.png"
                alt="ChatAlfa"
                className="h-32 w-auto object-contain p-0"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            {/* Canais Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown('canais')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button className="flex items-center text-sm font-medium text-gray-700 hover:text-[#3b82f6] transition-colors py-2">
                {t('nav.channels')}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openDropdown === 'canais' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'canais' && (
                <div className="absolute top-full left-0 w-80 bg-white rounded-xl shadow-xl border border-gray-100 py-4 mt-1 grid grid-cols-1 gap-1">
                  <Link to="/whatsapp" className="flex items-start gap-3 px-4 py-2 hover:bg-gray-50 transition-colors" onClick={() => setOpenDropdown(null)}>
                    <div className="w-10 h-10 bg-[#dcfce7] rounded-lg flex items-center justify-center flex-shrink-0"><MessageSquare className="h-5 w-5 text-[#22c55e]" /></div>
                    <div><p className="font-medium text-gray-900">WhatsApp</p><p className="text-xs text-gray-500">{t('nav.channels.whatsapp.desc')}</p></div>
                  </Link>
                  <Link to="/instagram" className="flex items-start gap-3 px-4 py-2 hover:bg-gray-50 transition-colors" onClick={() => setOpenDropdown(null)}>
                    <div className="w-10 h-10 bg-[#fce7f3] rounded-lg flex items-center justify-center flex-shrink-0"><Instagram className="h-5 w-5 text-[#db2777]" /></div>
                    <div><p className="font-medium text-gray-900">Instagram</p><p className="text-xs text-gray-500">{t('nav.channels.instagram.desc')}</p></div>
                  </Link>
                  <Link to="/facebook" className="flex items-start gap-3 px-4 py-2 hover:bg-gray-50 transition-colors" onClick={() => setOpenDropdown(null)}>
                    <div className="w-10 h-10 bg-[#dbeafe] rounded-lg flex items-center justify-center flex-shrink-0"><MessageSquare className="h-5 w-5 text-[#3b82f6]" /></div>
                    <div><p className="font-medium text-gray-900">Facebook</p><p className="text-xs text-gray-500">{t('nav.channels.facebook.desc')}</p></div>
                  </Link>
                  <Link to="/tiktok" className="flex items-start gap-3 px-4 py-2 hover:bg-gray-50 transition-colors" onClick={() => setOpenDropdown(null)}>
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0"><Zap className="h-5 w-5 text-black" /></div>
                    <div><p className="font-medium text-gray-900">TikTok</p><p className="text-xs text-gray-500">{t('nav.channels.tiktok.desc')}</p></div>
                  </Link>
                  <Link to="/website" className="flex items-start gap-3 px-4 py-2 hover:bg-gray-50 transition-colors" onClick={() => setOpenDropdown(null)}>
                    <div className="w-10 h-10 bg-[#fef9c3] rounded-lg flex items-center justify-center flex-shrink-0"><Bot className="h-5 w-5 text-[#ca8a04]" /></div>
                    <div><p className="font-medium text-gray-900">Website</p><p className="text-xs text-gray-500">{t('nav.channels.website.desc')}</p></div>
                  </Link>
                </div>
              )}
            </div>

            {/* Soluções Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown('solucoes')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button className="flex items-center text-sm font-medium text-gray-700 hover:text-[#3b82f6] transition-colors py-2">
                {t('nav.solutions')}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openDropdown === 'solucoes' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'solucoes' && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-[600px] bg-white rounded-xl shadow-xl border border-gray-100 p-6 mt-1 grid grid-cols-2 gap-8">
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">{t('nav.solutions.useCases')}</p>
                    <div className="space-y-1">
                      <Link to="/lead-qualification" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.leadQualification')}</Link>
                      <Link to="/booking-appointments" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.appointments')}</Link>
                      <Link to="/answer-faq" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.faqAnswers')}</Link>
                      <Link to="/placing-an-order" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.ordersAndSales')}</Link>
                      <Link to="/customer-support" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.customerSupport')}</Link>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">{t('nav.solutions.industries')}</p>
                    <div className="space-y-1">
                      <Link to="/ecommerce" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.ecommerce')}</Link>
                      <Link to="/real-estate" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.realEstate')}</Link>
                      <Link to="/education" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.education')}</Link>
                      <Link to="/healthcare" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.healthcare')}</Link>
                      <Link to="/aesthetic-beauty" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.beautyAesthetics')}</Link>
                      <Link to="/restaurants" className="block px-2 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.solutions.restaurants')}</Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Ferramentas Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown('ferramentas')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button className="flex items-center text-sm font-medium text-gray-700 hover:text-[#3b82f6] transition-colors py-2">
                {t('nav.tools')}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openDropdown === 'ferramentas' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'ferramentas' && (
                <div className="absolute top-full left-0 w-[480px] bg-white rounded-xl shadow-xl border border-gray-100 p-4 mt-1 grid grid-cols-2 gap-2">
                  <Link to="/whatsapp-capture-popup" className="block px-3 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.tools.capturePopup')}</Link>
                  <Link to="/click-to-chat-ads" className="block px-3 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.tools.clickToChatAds')}</Link>
                  <Link to="/story-replies" className="block px-3 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.tools.storyAutomation')}</Link>
                  <Link to="/whatsapp-chat-button" className="block px-3 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.tools.chatButton')}</Link>
                  <Link to="/comments-replies" className="block px-3 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.tools.commentReplies')}</Link>
                  <Link to="/qr-codes" className="block px-3 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.tools.qrCodes')}</Link>
                  <Link to="/reels-replies" className="block px-3 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.tools.reelsReplies')}</Link>
                  <Link to="/live-chat" className="block px-3 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.tools.liveChat')}</Link>
                  <Link to="/broadcasts" className="block px-3 py-2 text-sm text-gray-700 hover:text-[#3b82f6] hover:bg-gray-50 rounded-lg" onClick={() => setOpenDropdown(null)}>{t('nav.tools.broadcasts')}</Link>
                </div>
              )}
            </div>

            <Link to="/templates" className="text-sm font-medium text-gray-700 hover:text-[#3b82f6] transition-colors">
              {t('nav.templates')}
            </Link>

            {/* Recursos Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown('recursos')}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <button className="flex items-center text-sm font-medium text-gray-700 hover:text-[#3b82f6] transition-colors py-2">
                {t('nav.resources')}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openDropdown === 'recursos' ? 'rotate-180' : ''}`} />
              </button>
              {openDropdown === 'recursos' && (
                <div className="absolute top-full left-0 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 mt-1">
                  <Link to="/blog" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setOpenDropdown(null)}>{t('nav.resources.blog')}</Link>
                  <Link to="/docs" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setOpenDropdown(null)}>{t('nav.resources.docs')}</Link>
                  <Link to="/success-stories" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setOpenDropdown(null)}>{t('nav.resources.successStories')}</Link>
                  <Link to="/affiliate" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setOpenDropdown(null)}>{t('nav.resources.affiliateProgram')}</Link>
                </div>
              )}
            </div>

            <Link to="/integrations" className="text-sm font-medium text-gray-700 hover:text-[#3b82f6] transition-colors">
              {t('nav.integrations')}
            </Link>
          </div>

          {/* Right side buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Selector */}
            <div
              className="relative"
              onMouseEnter={() => setLangDropdownOpen(true)}
              onMouseLeave={() => setLangDropdownOpen(false)}
            >
              <button className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#3b82f6] transition-colors px-2 py-1 rounded-lg hover:bg-gray-50">
                <span className="text-base">{currentLanguage.flag}</span>
                <span className="text-xs font-medium">{currentLanguage.code === 'pt-BR' ? 'PT' : currentLanguage.code === 'es' ? 'ES' : 'EN'}</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {langDropdownOpen && (
                <div className="absolute top-full right-0 w-40 bg-white rounded-xl shadow-xl border border-gray-100 py-2 mt-1">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangDropdownOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${currentLanguage.code === lang.code ? 'text-[#3b82f6] font-medium' : 'text-gray-700'}`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="outline" className="rounded-full border-gray-300 text-gray-700 hover:bg-gray-50">
                    {t('nav.dashboard')}
                  </Button>
                </Link>
                <Button onClick={logout} variant="outline" className="rounded-full border-gray-300 text-gray-700 hover:bg-gray-50">
                  {t('nav.logout')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="rounded-full border-gray-300 text-gray-700 hover:bg-gray-50" onClick={login}>
                  {t('nav.login')}
                </Button>
                <Button className="rounded-full bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={login}>
                  {t('nav.startFree')}
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="h-6 w-6 text-gray-700" /> : <Menu className="h-6 w-6 text-gray-700" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col space-y-4 px-2">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase mb-2">{t('nav.channels')}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/whatsapp" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>WhatsApp</Link>
                  <Link to="/instagram" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>Instagram</Link>
                  <Link to="/facebook" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>Facebook</Link>
                  <Link to="/tiktok" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>TikTok</Link>
                  <Link to="/website" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>Website</Link>
                </div>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase mb-2">{t('nav.solutions')}</p>
                <div className="grid grid-cols-1 gap-2">
                  <Link to="/use-cases" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>{t('nav.solutions.useCases')}</Link>
                  <Link to="/templates" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>{t('nav.templates')}</Link>
                  <Link to="/ecommerce" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>{t('nav.solutions.ecommerce')}</Link>
                  <Link to="/real-estate" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>{t('nav.solutions.realEstate')}</Link>
                  <Link to="/restaurants" className="text-sm text-gray-700" onClick={() => setMobileMenuOpen(false)}>{t('nav.solutions.restaurants')}</Link>
                </div>
              </div>
              <Link to="/pricing" className="text-sm font-medium text-gray-700" onClick={() => setMobileMenuOpen(false)}>{t('nav.mobile.prices')}</Link>
              <Link to="/integrations" className="text-sm font-medium text-gray-700" onClick={() => setMobileMenuOpen(false)}>{t('nav.integrations')}</Link>

              {/* Language Selector Mobile */}
              <div className="flex gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${currentLanguage.code === lang.code ? 'bg-[#3b82f6] text-white' : 'bg-gray-100 text-gray-600'}`}
                  >
                    <span>{lang.flag}</span>
                    {lang.code === 'pt-BR' ? 'PT' : lang.code === 'es' ? 'ES' : 'EN'}
                  </button>
                ))}
              </div>

              <div className="pt-4 space-y-3">
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full rounded-full border-gray-300">{t('nav.dashboard')}</Button>
                    </Link>
                    <Button onClick={() => { logout(); setMobileMenuOpen(false); }} className="w-full rounded-full bg-red-500 hover:bg-red-600 text-white">{t('nav.logout')}</Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="w-full rounded-full border-gray-300" onClick={() => { login(); setMobileMenuOpen(false); }}>{t('nav.login')}</Button>
                    <Button className="rounded-full bg-[#3b82f6] hover:bg-[#2563eb] text-white w-full" onClick={() => { login(); setMobileMenuOpen(false); }}>{t('nav.startFree')}</Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
